import React, { useState, useCallback, useEffect } from 'react';
import { useMap } from '../map/MapContext';
import { useOgcServices, type FeatureEndpoints } from '../services/OgcServicesContext';
import { DatasetUploadPanel } from './DatasetUploadPanel';
import { getTileJson } from '../services/DatasetService';
import { FIR_BOUNDARIES_SOURCE_ID, FIXES_SOURCE_ID } from '../map/aviation-sources';
import { loadViewportFeatureOverlay } from '../services/map-overlay-loader';
import {
  applyOverlayState,
  loadOverlayState,
  loadOverlayStateSync,
  saveOverlayState,
  type OverlayState,
} from '../services/overlay-settings';
import {
  GPKG_RASTER_TILE_HELI_ROUTE_DATASET,
  GPKG_RASTER_TILE_HELI_ROUTE_LAYER,
  GPKG_RASTER_TILE_IFR_AREA_DATASET,
  GPKG_RASTER_TILE_IFR_AREA_LAYER,
  GPKG_RASTER_TILE_IFR_HIGH_DATASET,
  GPKG_RASTER_TILE_IFR_HIGH_LAYER,
  GPKG_RASTER_TILE_IFR_LOW_DATASET,
  GPKG_RASTER_TILE_IFR_LOW_LAYER,
  GPKG_RASTER_TILE_TAC_DATASET,
  GPKG_RASTER_TILE_TAC_LAYER,
  GPKG_RASTER_TILE_VFR_SECTIONAL_DATASET,
  GPKG_RASTER_TILE_VFR_SECTIONAL_LAYER,
  GPKG_VECTOR_TILE_AIRPORTS_DATASET,
  GPKG_VECTOR_TILE_AIRPORTS_LAYER,
  GPKG_VECTOR_TILE_AIRSPACES_DATASET,
  GPKG_VECTOR_TILE_AIRSPACES_LAYER,
  GPKG_VECTOR_TILE_AIRWAYS_DATASET,
  GPKG_VECTOR_TILE_AIRWAYS_LAYER,
  GPKG_VECTOR_TILE_NAVAIDS_DATASET,
  GPKG_VECTOR_TILE_NAVAIDS_LAYER,
  GPKG_VECTOR_TILE_OBSTACLES_DATASET,
  GPKG_VECTOR_TILE_OBSTACLES_LAYER,
} from '../config/environment';

// ─── Layer Catalogue (mirrors shared aviation-layers.ts hierarchy) ───────────

type Bounds = [number, number, number, number];

interface LayerDef {
  id: string;
  label: string;
  defaultVisible: boolean;
  defaultOpacity: number;
  dataset?: string;
  tileLayer?: string;
  fallbackBounds?: Bounds;
  maxZoom?: number;
  featureCollection?: keyof FeatureEndpoints;
  sourceId?: string;
}

interface SubGroup {
  label: string;
  layers: LayerDef[];
}

interface LayerGroup {
  label: string;
  subGroups: SubGroup[];
}

const CONUS_BOUNDS: Bounds = [-126, 23, -64, 51];
const NORTH_AMERICA_BOUNDS: Bounds = [-170, 15, -52, 72];

const chartLayer = (id: string, label: string, dataset: string, tileLayer: string, maxZoom: number): LayerDef => ({
  id,
  label,
  defaultVisible: false,
  defaultOpacity: 0.85,
  dataset,
  tileLayer,
  fallbackBounds: CONUS_BOUNDS,
  maxZoom,
});

const vectorLayer = (
  id: string,
  label: string,
  defaultVisible: boolean,
  dataset: string,
  tileLayer: string,
  fallbackBounds: Bounds = CONUS_BOUNDS,
): LayerDef => ({
  id,
  label,
  defaultVisible,
  defaultOpacity: 1,
  dataset,
  tileLayer,
  fallbackBounds,
  maxZoom: 9,
});

const AVIATION_GROUPS: LayerGroup[] = [
  {
    label: 'Raster Charts',
    subGroups: [
      { label: 'VFR', layers: [
        chartLayer('vfr-sectional', 'VFR Sectional', GPKG_RASTER_TILE_VFR_SECTIONAL_DATASET, GPKG_RASTER_TILE_VFR_SECTIONAL_LAYER, 8),
        chartLayer('tac', 'Terminal Area Chart', GPKG_RASTER_TILE_TAC_DATASET, GPKG_RASTER_TILE_TAC_LAYER, 10),
        chartLayer('heli-route', 'Helicopter Route', GPKG_RASTER_TILE_HELI_ROUTE_DATASET, GPKG_RASTER_TILE_HELI_ROUTE_LAYER, 10),
      ]},
      { label: 'IFR', layers: [
        chartLayer('ifr-low', 'IFR Low Enroute', GPKG_RASTER_TILE_IFR_LOW_DATASET, GPKG_RASTER_TILE_IFR_LOW_LAYER, 8),
        chartLayer('ifr-high', 'IFR High Enroute', GPKG_RASTER_TILE_IFR_HIGH_DATASET, GPKG_RASTER_TILE_IFR_HIGH_LAYER, 7),
        chartLayer('ifr-area', 'IFR Area', GPKG_RASTER_TILE_IFR_AREA_DATASET, GPKG_RASTER_TILE_IFR_AREA_LAYER, 9),
      ]},
    ],
  },
  {
    label: 'Vector Features',
    subGroups: [
      { label: 'Airspace', layers: [
        vectorLayer('airspace-class-b', 'Class B', true, GPKG_VECTOR_TILE_AIRSPACES_DATASET, GPKG_VECTOR_TILE_AIRSPACES_LAYER),
        vectorLayer('airspace-class-c', 'Class C', true, GPKG_VECTOR_TILE_AIRSPACES_DATASET, GPKG_VECTOR_TILE_AIRSPACES_LAYER),
        vectorLayer('airspace-class-d', 'Class D', true, GPKG_VECTOR_TILE_AIRSPACES_DATASET, GPKG_VECTOR_TILE_AIRSPACES_LAYER),
        vectorLayer('airspace-class-e', 'Class E', false, GPKG_VECTOR_TILE_AIRSPACES_DATASET, GPKG_VECTOR_TILE_AIRSPACES_LAYER),
      ]},
      { label: 'Special Use Airspace', layers: [
        vectorLayer('airspace-moa', 'MOA', true, GPKG_VECTOR_TILE_AIRSPACES_DATASET, GPKG_VECTOR_TILE_AIRSPACES_LAYER),
        vectorLayer('airspace-restricted', 'Restricted', true, GPKG_VECTOR_TILE_AIRSPACES_DATASET, GPKG_VECTOR_TILE_AIRSPACES_LAYER),
        vectorLayer('airspace-prohibited', 'Prohibited', true, GPKG_VECTOR_TILE_AIRSPACES_DATASET, GPKG_VECTOR_TILE_AIRSPACES_LAYER),
        vectorLayer('airspace-warning', 'Warning', false, GPKG_VECTOR_TILE_AIRSPACES_DATASET, GPKG_VECTOR_TILE_AIRSPACES_LAYER),
        vectorLayer('airspace-alert', 'Alert', false, GPKG_VECTOR_TILE_AIRSPACES_DATASET, GPKG_VECTOR_TILE_AIRSPACES_LAYER),
        vectorLayer('airspace-tfr', 'TFR', true, GPKG_VECTOR_TILE_AIRSPACES_DATASET, GPKG_VECTOR_TILE_AIRSPACES_LAYER),
      ]},
      { label: 'Infrastructure', layers: [
        vectorLayer('airports-symbol', 'Airports', true, GPKG_VECTOR_TILE_AIRPORTS_DATASET, GPKG_VECTOR_TILE_AIRPORTS_LAYER),
        vectorLayer('obstacles-symbol', 'Obstacles', false, GPKG_VECTOR_TILE_OBSTACLES_DATASET, GPKG_VECTOR_TILE_OBSTACLES_LAYER, NORTH_AMERICA_BOUNDS),
        vectorLayer('navaids-symbol', 'Navaids', false, GPKG_VECTOR_TILE_NAVAIDS_DATASET, GPKG_VECTOR_TILE_NAVAIDS_LAYER),
      ]},
      { label: 'Navigation', layers: [
        vectorLayer('airways', 'Airways', false, GPKG_VECTOR_TILE_AIRWAYS_DATASET, GPKG_VECTOR_TILE_AIRWAYS_LAYER),
        { id: 'fixes', label: 'Fixes', defaultVisible: false, defaultOpacity: 1, fallbackBounds: NORTH_AMERICA_BOUNDS, maxZoom: 9, featureCollection: 'fixes', sourceId: FIXES_SOURCE_ID },
        { id: 'fir-boundaries', label: 'FIR / UIR Boundaries', defaultVisible: false, defaultOpacity: 1, fallbackBounds: NORTH_AMERICA_BOUNDS, maxZoom: 6, featureCollection: 'firBoundaries', sourceId: FIR_BOUNDARIES_SOURCE_ID },
      ]},
    ],
  },
  {
    label: 'Weather',
    subGroups: [
      { label: 'Radar & Advisories', layers: [
        { id: 'nexrad-radar', label: 'NEXRAD Radar', defaultVisible: false, defaultOpacity: 0.6, dataset: 'weather', tileLayer: 'nexrad', fallbackBounds: CONUS_BOUNDS, maxZoom: 7 },
        { id: 'sigmets-fill', label: 'SIGMETs', defaultVisible: false, defaultOpacity: 0.2, fallbackBounds: NORTH_AMERICA_BOUNDS, maxZoom: 6 },
        { id: 'airmets-fill', label: 'AIRMETs', defaultVisible: false, defaultOpacity: 0.15, fallbackBounds: NORTH_AMERICA_BOUNDS, maxZoom: 6 },
      ]},
    ],
  },
  {
    label: 'Live Traffic',
    subGroups: [
      { label: 'ADS-B', layers: [
        { id: 'adsb-traffic',       label: 'Traffic Targets', defaultVisible: false, defaultOpacity: 1 },
        { id: 'adsb-traffic-label', label: 'Traffic Labels',  defaultVisible: false, defaultOpacity: 1 },
      ]},
    ],
  },
];

// ─── State ───────────────────────────────────────────────────────────────────

type LayerState = OverlayState;

function buildInitialState(): LayerState {
  const state: LayerState = {};
  for (const g of AVIATION_GROUPS) {
    for (const sg of g.subGroups) {
      for (const l of sg.layers) {
        state[l.id] = { visible: l.defaultVisible, opacity: l.defaultOpacity };
      }
    }
  }
  return state;
}

function isValidBounds(bounds: unknown): bounds is Bounds {
  return Array.isArray(bounds)
    && bounds.length === 4
    && bounds.every((value) => typeof value === 'number' && Number.isFinite(value))
    && bounds[0] < bounds[2]
    && bounds[1] < bounds[3]
    && bounds[0] >= -180
    && bounds[2] <= 180
    && bounds[1] >= -90
    && bounds[3] <= 90;
}

export const AviationOverlayPanel: React.FC = () => {
  const { map } = useMap();
  const { featuresClient } = useOgcServices();
  const [state, setState] = useState<LayerState>(() => loadOverlayStateSync(buildInitialState()));
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [featureLoads, setFeatureLoads] = useState<Record<string, { loading: boolean; count?: number; error?: string }>>({});

  useEffect(() => {
    let cancelled = false;
    void loadOverlayState(buildInitialState()).then((saved) => {
      if (!cancelled) setState(saved);
    });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    void saveOverlayState(state).catch(() => { /* local fallback remains available */ });
    if (map) applyOverlayState(map, state);
  }, [map, state]);

  const toggleExpand = useCallback((key: string) => {
    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  const loadFeatureLayer = useCallback(async (layer: LayerDef) => {
    if (!map || !layer.featureCollection || !layer.sourceId) return;
    setFeatureLoads((prev) => ({ ...prev, [layer.id]: { loading: true } }));
    try {
      const count = await loadViewportFeatureOverlay(map, featuresClient, layer.featureCollection, layer.sourceId);
      setFeatureLoads((prev) => ({ ...prev, [layer.id]: { loading: false, count } }));
    } catch {
      setFeatureLoads((prev) => ({ ...prev, [layer.id]: { loading: false, error: 'Data unavailable' } }));
    }
  }, [map, featuresClient]);

  const toggleLayer = useCallback((layer: LayerDef) => {
    const nextVisible = !state[layer.id]?.visible;
    setState((prev) => ({
      ...prev,
      [layer.id]: { ...prev[layer.id], visible: nextVisible },
    }));
    if (map) {
      const visibility = nextVisible ? 'visible' : 'none';
      try { map.setLayoutProperty(layer.id, 'visibility', visibility); } catch { /* layer may not exist yet */ }
      for (const suffix of ['-fill', '-line', '-circle', '-label']) {
        try { map.setLayoutProperty(`${layer.id}${suffix}`, 'visibility', visibility); } catch { /* ok */ }
      }
    }
    if (nextVisible) void loadFeatureLayer(layer);
  }, [map, state, loadFeatureLayer]);

  const setOpacity = useCallback((id: string, delta: number) => {
    setState((prev) => {
      const current = prev[id]?.opacity ?? 1;
      const next = Math.max(0, Math.min(1, current + delta));
      if (map) {
        try { map.setPaintProperty(id, 'raster-opacity', next); } catch { /* ok */ }
        try { map.setPaintProperty(id, 'fill-opacity', next); } catch { /* ok */ }
      }
      return { ...prev, [id]: { ...prev[id], opacity: next } };
    });
  }, [map]);

  const zoomToLayer = useCallback(async (layer: LayerDef) => {
    if (!map) return;

    let bounds = layer.fallbackBounds;
    if (layer.dataset && layer.tileLayer) {
      const tileJson = await getTileJson(layer.dataset, layer.tileLayer);
      if (isValidBounds(tileJson?.bounds)) {
        bounds = tileJson.bounds;
      }
    }

    if (!bounds) return;
    map.fitBounds(
      [[bounds[0], bounds[1]], [bounds[2], bounds[3]]],
      { padding: 70, duration: 900, maxZoom: layer.maxZoom ?? 9 },
    );
  }, [map]);

  return (
    <div>
      <DatasetUploadPanel />
      {AVIATION_GROUPS.map((group) => {
        const gKey = group.label;
        const gOpen = expanded[gKey] !== false; // default open
        return (
          <div key={gKey} className="panel-section">
            <div className="collapsible-header" onClick={() => toggleExpand(gKey)}>
              <span className={`chevron ${gOpen ? 'open' : ''}`}>▶</span>
              <span className="panel-section-title" style={{ margin: 0 }}>{group.label}</span>
            </div>
            {gOpen && group.subGroups.map((sg) => {
              const sgKey = `${gKey}/${sg.label}`;
              const sgOpen = expanded[sgKey] !== false;
              return (
                <div key={sgKey} className="collapsible-body">
                  <div className="collapsible-header" onClick={() => toggleExpand(sgKey)}>
                    <span className={`chevron ${sgOpen ? 'open' : ''}`}>▶</span>
                    <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{sg.label}</span>
                  </div>
                  {sgOpen && sg.layers.map((layer) => {
                    const ls = state[layer.id];
                    return (
                      <div key={layer.id} className="list-item" style={{ paddingLeft: 8 }}>
                        <label className="toggle" style={{ flex: 1 }}>
                          <span
                            className={`toggle-box ${ls?.visible ? 'checked' : ''}`}
                            onClick={(e) => { e.preventDefault(); toggleLayer(layer); }}
                          />
                          <span style={{ fontSize: 13 }}>
                            {layer.label}
                            {featureLoads[layer.id]?.loading && <small style={{ color: 'var(--text-muted)' }}> · loading</small>}
                            {featureLoads[layer.id]?.count !== undefined && <small style={{ color: 'var(--text-muted)' }}> · {featureLoads[layer.id].count}</small>}
                            {featureLoads[layer.id]?.error && <small className="status-yellow"> · unavailable</small>}
                          </span>
                        </label>
                        {ls && ls.opacity < 1 && (
                          <div className="opacity-row" style={{ width: 90 }}>
                            <button className="btn-icon" style={{ width: 20, height: 20, fontSize: 12 }} onClick={() => setOpacity(layer.id, -0.1)}>−</button>
                            <div className="opacity-bar"><div className="opacity-fill" style={{ width: `${ls.opacity * 100}%` }} /></div>
                            <button className="btn-icon" style={{ width: 20, height: 20, fontSize: 12 }} onClick={() => setOpacity(layer.id, 0.1)}>+</button>
                          </div>
                        )}
                        <button
                          className="btn-icon"
                          style={{ width: 24, height: 24, fontSize: 13 }}
                          title={`Zoom to ${layer.label}`}
                          aria-label={`Zoom to ${layer.label}`}
                          onClick={() => zoomToLayer(layer)}
                          disabled={!map || (!layer.fallbackBounds && (!layer.dataset || !layer.tileLayer))}
                        >
                          ⌖
                        </button>
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
};
